import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react'

/**
 * What a guided grid's save answers with.
 *
 * `runGuidedSaves` holds the wizard on the step when `failed` is above zero, so
 * a grid that answers `undefined` is read as a clean save — which is how a
 * refused Hydraulic save still let the user walk on to ET Reference
 * (SCRUM-1032 QC round 6, item 4). Every guided save returns this, validation
 * refusals included.
 */
export interface GuidedSaveResult {
  /** Rows refused or not written. Above zero holds the step. */
  failed: number
  /** Rows the pass set out to write. */
  total: number
  /**
   * Writes sent and still unanswered (a slow controller). Not refused, so
   * leaving must not call them discarded; not saved either, so Next holds.
   */
  stillOut?: number
}

type SaveFn = (() => Promise<unknown> | unknown) & {
  /**
   * This grid's flush limit, overriding `GUIDED_SAVE_TIMEOUT_MS`; null for
   * none. For a grid whose flush is long by nature and bounded by its own
   * request timeouts — the Station flush runs for minutes on a control hub.
   */
  timeoutMs?: number | null
}

export interface GuidedEditOptions {
  /** See `SaveFn.timeoutMs`. */
  timeoutMs?: number | null
}

interface GuidedEditValue {
  /** Grids start in bulk edit rather than read-only. */
  bulkEditByDefault: boolean
  registerSave: (key: string, save: SaveFn | null) => void
  /**
   * A grid saying it is not done, and why. The wizard reads this to hold
   * Finish: leaving with rows still to fill in is how a setup ends up
   * half-entered, and the step cannot tell the wizard that through a save.
   */
  setIncomplete: (key: string, reason: string | null) => void
  /**
   * SCRUM-1032 — a grid (`source`, one id per grid) reporting on its
   * background write.
   *
   * The wizard shows one status for whichever step is open, instead of each
   * grid raising its own success toast: entering six plants raised six toasts,
   * and they drowned out the errors that mattered. Tracked per grid — the
   * Plant / Soil / Emitter step runs three at once.
   */
  reportAutosave: (event: AutosaveEvent, source: string) => void
  /**
   * A grid saying whether it has anything staged, written or not. Cheap — a
   * set update, no re-render — and read only when the page is about to be
   * left by the browser (reload, tab close, browser Back), which flushes
   * nothing.
   */
  reportPending?: (source: string, pending: boolean) => void
}

/** What the wizard shows about the step's own background writes. */
export type GuidedAutosaveState = 'idle' | 'saving' | 'saved' | 'error'

/** True only inside the guided wizard; used to gate wizard-only affordances. */
export const useIsGuidedSetup = () => useContext(GuidedEditContext) !== null

const GuidedEditContext = createContext<GuidedEditValue | null>(null)

interface GuidedEditProviderProps {
  bulkEditByDefault?: boolean
  /** Registry the host owns, so it can flush saves from outside the provider. */
  savesRef: SavesRef
  /** Where the grids report unfinished work, for the wizard to act on. */
  incompleteRef?: IncompleteRef
  /** Told whenever that changes, since a ref alone cannot re-render the host. */
  onIncompleteChange?: () => void
  /** Told when the open step starts and finishes writing in the background. */
  onAutosaveStateChange?: (state: GuidedAutosaveState) => void
  /**
   * Kept true while any grid has work that is not safely on the server:
   * staged, mid-write, refused, or held. Read by the wizard's `beforeunload`
   * guard; a ref so reporting it never re-renders the wizard.
   */
  unsavedRef?: UnsavedRef
  children: ReactNode
}

export type UnsavedRef = { current: boolean }

export type SavesRef = { current: Map<string, SaveFn> }

/** Reasons a step is not finished, by grid key. Owned by the wizard. */
export type IncompleteRef = { current: Map<string, string> }

export const useGuidedIncompleteRef = (): IncompleteRef =>
  useRef(new Map<string, string>())

/** Registry for the grid saves; created by the wizard, filled by the grids. */
export const useGuidedSavesRef = (): SavesRef =>
  useRef(new Map<string, SaveFn>())

/**
 * How long one grid's flush may run before the wizard stops waiting for it.
 *
 * The API client has no timeout, so a write the server never answers kept
 * `runGuidedSaves` waiting for ever — Back and Next stayed disabled and Exit
 * did nothing (SCRUM-1032 wizard review). A timeout reads as a failure: Next
 * holds, Back and Exit leave and say so. The write itself is not cancelled;
 * a later flush queues behind it in the grid's own serialised save.
 */
export const GUIDED_SAVE_TIMEOUT_MS = 30_000

/** Thrown by `runGuidedSaves` when a grid's flush did not answer in time. */
export class GuidedSaveTimeoutError extends Error {
  constructor() {
    super('A save did not answer in time')
    this.name = 'GuidedSaveTimeoutError'
  }
}

const withTimeout = (save: SaveFn, timeoutMs: number): Promise<unknown> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new GuidedSaveTimeoutError()), timeoutMs)
    // Inside the executor, so a save that throws synchronously rejects.
    Promise.resolve(save()).then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (error) => {
        clearTimeout(timer)
        reject(error)
      },
    )
  })

/** Thrown by `runGuidedSaves` when any grid left rows unwritten. */
export class GuidedSaveIncompleteError extends Error {
  constructor(public readonly failed: number) {
    super(`${failed} row(s) were not written`)
    this.name = 'GuidedSaveIncompleteError'
  }
}

/**
 * Runs every registered grid save, one after another.
 *
 * Sequential rather than parallel: several of these grids write row by row and
 * the API 500s on concurrent writes.
 */
export const runGuidedSaves = async (
  savesRef: SavesRef,
  { timeoutMs = GUIDED_SAVE_TIMEOUT_MS }: { timeoutMs?: number } = {},
) => {
  /**
   * SCRUM-1032 QC round 7 review — every grid is flushed, then the answer is
   * given once.
   *
   * Stopping at the first refused grid left the others unwritten, and Back,
   * Exit and an earlier step still go ahead after a refusal — so on the Plant /
   * Soil / Emitter step a refused plant cost the user a soil rename typed a
   * moment before, with no word about it.
   */
  let totalFailed = 0
  let totalStillOut = 0
  let firstError: unknown = null
  let hasError = false
  for (const save of Array.from(savesRef.current.values())) {
    let result: { failed?: unknown; stillOut?: unknown } | undefined
    try {
      const limit = save.timeoutMs === undefined ? timeoutMs : save.timeoutMs
      result = (await (limit === null
        ? Promise.resolve().then(() => save())
        : withTimeout(save, limit))) as
        | { failed?: unknown; stillOut?: unknown }
        | undefined
    } catch (error) {
      if (!hasError) firstError = error
      hasError = true
      continue
    }

    /**
     * SCRUM-1032 QC item 14 — the step does not move on from a refused save.
     *
     * The grids all answer with `{ failed, total }`, and that was ignored: an
     * ET Reference row rejected for a missing description raised its toast, the
     * wizard walked on to the next step regardless, and the row was gone. The
     * step was then ticked complete, so nothing downstream said otherwise.
     *
     * Thrown rather than returned, because the caller already treats a throw as
     * "stay put" — a save that raises and one that reports failures mean the
     * same thing to the user.
     */
    const failed = Number(result?.failed ?? 0)
    if (Number.isFinite(failed) && failed > 0) totalFailed += failed
    const stillOut = Number(result?.stillOut ?? 0)
    if (Number.isFinite(stillOut) && stillOut > 0) totalStillOut += stillOut
  }

  if (hasError) throw firstError
  if (totalFailed > 0) throw new GuidedSaveIncompleteError(totalFailed)
  // Sent and not answered yet: the same as a save that ran out of time —
  // Next holds ("taking longer"), leaving says it may not have been saved,
  // never that it was discarded (D2 retest: the write landed 20 s later).
  if (totalStillOut > 0) throw new GuidedSaveTimeoutError()
}

/**
 * What a grid tells the wizard about its background write. 'held' is a clean
 * write that left something staged (a row added while it ran); 'clear' is the
 * grid having nothing left to save; 'leave' is the grid unmounting, which
 * also ends any write of its that is still out.
 */
export type AutosaveEvent = 'start' | 'done' | 'held' | 'error' | 'clear' | 'leave'

/** Which grids are mid-write, and which grids are not done. */
export interface AutosaveBatch {
  /**
   * Grids with a write out. By grid rather than counted: a grid that leaves
   * the step mid-write never reports back, and a count it never lowered held
   * "Saving…" on every later step (round 7 follow-up review).
   */
  active: ReadonlySet<string>
  /** Grids whose last write was refused. */
  failing: ReadonlySet<string>
  /** Grids whose last write was clean but left rows staged. */
  held: ReadonlySet<string>
}

export const emptyAutosaveBatch = (): AutosaveBatch => ({
  active: new Set(),
  failing: new Set(),
  held: new Set(),
})

/**
 * Whether anything a grid holds is not safely on the server: staged, being
 * written, refused, or left staged by a clean write.
 */
export const hasUnsavedGuidedWork = (
  batch: AutosaveBatch,
  pendingSources: ReadonlySet<string>,
): boolean =>
  pendingSources.size > 0 ||
  batch.active.size > 0 ||
  batch.failing.size > 0 ||
  batch.held.size > 0

const statusAfter = (batch: AutosaveBatch): GuidedAutosaveState => {
  if (batch.failing.size > 0) return 'error'
  // Nothing to claim while a grid still has staged rows (round 7 follow-up:
  // Plants read "All changes saved" beside a plant added mid-save).
  if (batch.held.size > 0) return 'idle'
  return 'saved'
}

/**
 * One autosave event from one grid, with the status to show when one is due.
 *
 * SCRUM-1032 QC round 7 review — a failure has to outlive the grids that are
 * still writing. Counted alone, a refused plant save only lowered the count;
 * the soil save that finished after it brought the count to zero and the step
 * read "All changes saved" beside the plant's own error.
 *
 * Kept per grid rather than as one flag, so a failure ends with the thing that
 * caused it: the grid saving cleanly, or having nothing left to save (the
 * refused row deleted or discarded — 'clear'), or going away ('leave'). One
 * flag left "Could not save" on screen after the refused pipe was gone.
 */
export const applyAutosaveEvent = (
  batch: AutosaveBatch,
  event: AutosaveEvent,
  source: string,
): { batch: AutosaveBatch; status: GuidedAutosaveState | null } => {
  const active = new Set(batch.active)
  const failing = new Set(batch.failing)
  const held = new Set(batch.held)

  if (event === 'start') {
    active.add(source)
    // A refusal still standing stays on screen while another grid writes.
    return {
      batch: { active, failing, held },
      status: failing.size > 0 ? null : 'saving',
    }
  }

  if (event === 'clear' || event === 'leave') {
    const wasActive = event === 'leave' && active.delete(source)
    const wasFailing = failing.delete(source)
    const wasHeld = held.delete(source)
    if (!wasActive && !wasFailing && !wasHeld) return { batch, status: null }
    const next = { active, failing, held }
    // Still writing: the batch reports when it ends.
    if (active.size > 0) return { batch: next, status: null }
    // Only an error going, or the last write going with its grid, changes
    // what is on screen. Never "saved": nothing was saved by leaving.
    if (!wasFailing && !wasActive) return { batch: next, status: null }
    return { batch: next, status: failing.size > 0 ? 'error' : 'idle' }
  }

  active.delete(source)
  failing.delete(source)
  held.delete(source)
  if (event === 'error') failing.add(source)
  if (event === 'held') held.add(source)
  const next = { active, failing, held }
  if (active.size > 0) return { batch: next, status: null }
  return { batch: next, status: statusAfter(next) }
}

/**
 * SCRUM-1032 — guided setup's contract with the config grids it embeds.
 *
 * The wizard wants two things the /config screens do not do on their own: open
 * straight into bulk edit (the guided flow exists to enter data), and flush
 * whatever is pending when the user hits Next. Rather than threading a pair of
 * props through every step wrapper — Plant/Soil/Emitter alone renders three
 * grids — each grid registers its own save here and reads the default from the
 * same place. Outside the wizard the context is absent and every hook below is
 * inert, so /config behaves exactly as before.
 */
export const GuidedEditProvider = ({
  bulkEditByDefault = false,
  savesRef,
  incompleteRef,
  onIncompleteChange,
  onAutosaveStateChange,
  unsavedRef,
  children,
}: GuidedEditProviderProps) => {
  const registerSave = useCallback(
    (key: string, save: SaveFn | null) => {
      if (save) savesRef.current.set(key, save)
      else savesRef.current.delete(key)
    },
    [savesRef],
  )

  const setIncomplete = useCallback(
    (key: string, reason: string | null) => {
      if (!incompleteRef) return
      const previous = incompleteRef.current.get(key) ?? null
      if (previous === reason) return
      if (reason) incompleteRef.current.set(key, reason)
      else incompleteRef.current.delete(key)
      onIncompleteChange?.()
    },
    [incompleteRef, onIncompleteChange],
  )

  // Which grids are mid-write, refused or holding rows, by grid: the Plant /
  // Soil / Emitter step has three, and one of them finishing must not clear a
  // status the others are still earning. See `applyAutosaveEvent`.
  const batchRef = useRef<AutosaveBatch>(emptyAutosaveBatch())
  const pendingRef = useRef(new Set<string>())
  const syncUnsaved = useCallback(() => {
    if (unsavedRef) {
      unsavedRef.current = hasUnsavedGuidedWork(batchRef.current, pendingRef.current)
    }
  }, [unsavedRef])
  const reportAutosave = useCallback(
    (event: AutosaveEvent, source: string) => {
      const { batch, status } = applyAutosaveEvent(
        batchRef.current,
        event,
        source,
      )
      batchRef.current = batch
      syncUnsaved()
      if (status) onAutosaveStateChange?.(status)
    },
    [onAutosaveStateChange, syncUnsaved],
  )
  const reportPending = useCallback(
    (source: string, pending: boolean) => {
      if (pending) pendingRef.current.add(source)
      else pendingRef.current.delete(source)
      syncUnsaved()
    },
    [syncUnsaved],
  )

  const value = useMemo(
    () => ({
      bulkEditByDefault,
      registerSave,
      setIncomplete,
      reportAutosave,
      reportPending,
    }),
    [bulkEditByDefault, registerSave, setIncomplete, reportAutosave, reportPending],
  )

  return (
    <GuidedEditContext.Provider value={value}>
      {children}
    </GuidedEditContext.Provider>
  )
}

/**
 * Registers a grid's bulk-edit save with the guided wizard and reports whether
 * bulk edit should be on from the start.
 *
 * Returns false everywhere except inside the wizard.
 */
export const useGuidedEdit = (
  key: string,
  save: SaveFn,
  options?: GuidedEditOptions,
) => {
  const ctx = useContext(GuidedEditContext)

  // Kept in a ref so a save closing over fresh state does not re-register on
  // every render.
  const saveRef = useRef(save)
  saveRef.current = save
  const timeoutMs = options?.timeoutMs

  useEffect(() => {
    if (!ctx) return
    const registered: SaveFn = () => saveRef.current()
    if (timeoutMs !== undefined) registered.timeoutMs = timeoutMs
    ctx.registerSave(key, registered)
    return () => ctx.registerSave(key, null)
  }, [ctx, key, timeoutMs])

  return ctx?.bulkEditByDefault ?? false
}

/**
 * Reports that this grid still has work in it, with a reason the wizard can
 * show. Pass null once it is done. Inert outside the wizard.
 */
export const useGuidedIncomplete = (key: string, reason: string | null) => {
  const ctx = useContext(GuidedEditContext)

  useEffect(() => {
    if (!ctx) return
    ctx.setIncomplete(key, reason)
    return () => ctx.setIncomplete(key, null)
  }, [ctx, key, reason])
}

/**
 * The reporter, for `useGuidedAutosave`. Absent outside the wizard, where a
 * grid's own toast is still the feedback.
 */
export const useGuidedAutosaveReporter = () =>
  useContext(GuidedEditContext)?.reportAutosave

/**
 * The pending reporter, for `useGuidedAutosave`. Used by every grid, including
 * the ones that show their own save status: the browser leaving loses their
 * staged rows just the same.
 */
export const useGuidedPendingReporter = () =>
  useContext(GuidedEditContext)?.reportPending

export default GuidedEditContext
